import { Breadcrumb } from "@enduring-systems/react-ui";
import { Link, useLocation } from "react-router-dom";

import { OrgSwitcher } from "@/features/organization";
import { useSession } from "@/features/organization/useSession";

// Map of the first path segment → display label. Falls back to title-case
// of the segment when not listed (so new routes Just Work as crumbs).
const SEGMENT_LABELS: Record<string, string> = {
  "": "Dashboard",
  waitlist: "Waitlist",
  users: "Users",
  organization: "Organization",
  devices: "Devices",
  "feature-flags": "Feature Flags",
  assistant: "Assistant",
  "usage-metrics": "Usage Metrics",
  "audit-log": "Audit Log",
  "conversation-logs": "Conversation Logs",
  "guardrail-metrics": "Guardrail Metrics",
  "rag-quality": "RAG Quality",
  "test-inbox": "Test Inbox",
  settings: "Settings",
};

function titleCase(segment: string): string {
  return segment
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function labelFor(segment: string): string {
  return SEGMENT_LABELS[segment] ?? titleCase(segment);
}

export function Breadcrumbs(): React.ReactElement {
  const { pathname } = useLocation();
  const { session } = useSession();
  const hasOrg = Boolean(session?.activeOrgUnit);
  const segments = pathname.split("/").filter(Boolean);
  // Always treat the root as Dashboard so we have at least one crumb.
  const crumbs = segments.length === 0 ? [""] : segments;

  return (
    <Breadcrumb data-testid="breadcrumbs">
      <Breadcrumb.List>
        {/* Only render the OrgSwitcher crumb when a session has an
            active org. Otherwise we'd leave an empty <li> in the list
            and a leading separator before the first route crumb. */}
        {hasOrg ? (
          <Breadcrumb.Item>
            <OrgSwitcher />
          </Breadcrumb.Item>
        ) : null}
        {crumbs.map((segment, idx) => {
          const isLast = idx === crumbs.length - 1;
          const path =
            "/" +
            crumbs
              .slice(0, idx + 1)
              .filter(Boolean)
              .join("/");
          // Leading separator only when this crumb has a sibling before
          // it — either the org crumb, or a prior route crumb.
          const showSeparator = hasOrg || idx > 0;
          return (
            // Separator + the crumb itself live inside a single
            // Breadcrumb.Item so .es-breadcrumb__list (<ol>) only ever
            // contains <li> children. The separator is presentational
            // and a sibling of the link/page within the item.
            <Breadcrumb.Item key={`${segment}-${idx}`}>
              {showSeparator ? <Breadcrumb.Separator /> : null}
              {isLast ? (
                <Breadcrumb.Page>{labelFor(segment)}</Breadcrumb.Page>
              ) : (
                <Link to={path} className="es-breadcrumb__link">
                  {labelFor(segment)}
                </Link>
              )}
            </Breadcrumb.Item>
          );
        })}
      </Breadcrumb.List>
    </Breadcrumb>
  );
}
